import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { List, Set } from 'immutable';
import { Observable, of, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { Account } from '../../../account-data/account.model';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../../../dialog/dialog.service';
import {
  ALL_LIST_FILTER,
  ISearchParams,
  SEARCH_ENTITY_ROUTE,
  SEARCH_LIST_FILTER,
} from '../../../entity-data/entity-data.data';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { CurrentPlan } from '../../../features-data/sub-models/current-plan.model';
import { NavigationService } from '../../../navigation/navigation.service';
import { Settings } from '../../../settings-data/settings.model';
import { GlobalHeaderThemesEnum } from '../../../shared/components/global-header/global-header-themes.enum';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { IRouter } from '../../../shared/interfaces/router.interface';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import {
  FeedbackScreenEnum,
  IFeedbackCard,
} from '../../../feedback-data/feedback-data.data';
import { FeedbackDataStore } from '../../../feedback-data/feedback-data.store';
import { IBulkAction } from '../../entity-master.interface';
import { EntityMasterBuilder } from './entity-master.builder';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { Toggles } from '../../../features-data/sub-models/toggles.model';

@Component({
  selector: 'app-entity-master',
  templateUrl: 'entity-master.component.html',
  styleUrls: ['entity-master.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [EntityMasterBuilder],
})
export class EntityMasterComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() header: string;
  @Input() primaryAction: IRouter;
  @Input() filters: List<IRouter>;
  @Input() activeFilter: string;
  @Input() featureName: string;
  @Input() enableSearch: boolean;
  @Input() searchPlaceholder: string;
  @Input() defaultSearchParams: ISearchParams;
  @Input() searchQuery: string;
  @Input() selectedEntityBodies: Set<any>;
  @Input() enableBulkActions: boolean;
  @Input() bulkDeleteConfirmTitleKey: string;
  @Input() bulkDeleteConfirmPromptKey: string;
  @Input() bulkActionSingleTranslateKey: string;
  @Input() bulkActionPluralTranslateKey: string;
  @Input() bulkActions: List<IBulkAction>;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAdditionalProperties: ITrackingProperties;
  @Input() account: Account;
  @Input() enableFeedbackCard: boolean;
  @Input() feedbackCardInfo: IFeedbackCard;
  @Input() feedbackScreenName: FeedbackScreenEnum;
  @Input() settings: Settings;
  @Input() currentPlan: CurrentPlan;
  @Input() userEmail: string;

  @Output() onBulkAction: EventEmitter<string> = new EventEmitter<string>();
  @Output() onDeleteConfirmed: EventEmitter<void> = new EventEmitter<void>();
  @Output() onPrimaryAction: EventEmitter<void> = new EventEmitter<void>();

  private _listModeUrl: string;
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _trackingScreenName: TrackingScreenNamesEnum;

  isIaNavEnabled$: Observable<boolean>;
  headerTheme$: Observable<GlobalHeaderThemesEnum>;
  featureHighlightIcon: IIcon;
  bulkActionPrimaryTrackingAction: ITrackingAction;
  bulkActionSecondaryTrackingAction: ITrackingAction;
  bulkTrackingAdditionalProperties: { count: number; tab_identifier?: string };
  deleleConfirmDialogId: string;
  searchQueryValue: string;
  showFeedbackCard$: Observable<boolean>;
  deleteConfirmTrackingDialogName: string;

  constructor(
    private _dialogService: DialogService,
    private _trackingDispatcher: TrackingDispatcher,
    private _featuresDataStore: FeaturesDataStore,
    private _navigationService: NavigationService,
    private _entityMasterBuilder: EntityMasterBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _feedbackDataStore: FeedbackDataStore,
    private _route: ActivatedRoute
  ) {}

  ngOnChanges(changes) {
    if (changes.searchQuery) {
      this.searchQueryValue = this.searchQuery;
    }

    if (changes.selectedEntityBodies) {
      if (
        this.enableBulkActions &&
        this.selectedEntityBodies &&
        this.selectedEntityBodies.size > 0
      ) {
        const tabIdentifier = ![ALL_LIST_FILTER, SEARCH_LIST_FILTER].includes(
          this.activeFilter
        )
          ? this.activeFilter
          : 'none';
        const tabTrackingProps =
          (this.activeFilter && { tab_identifier: tabIdentifier }) || {};

        this.bulkTrackingAdditionalProperties = {
          ...tabTrackingProps,
          count: this.selectedEntityBodies.size,
        };
      }
    }
  }

  ngOnInit() {
    this._listModeUrl = null;
    this._trackingScreenName = this.trackingScreenName;

    this.deleleConfirmDialogId = v4();
    this.deleteConfirmTrackingDialogName =
      TrackingDialogNamesEnum.delete_confirmation;
    this.showFeedbackCard$ = this.enableFeedbackCard
      ? this._feedbackDataStore
          .getFeedbackHiddenForFeatureNameForAccount(
            this.featureName,
            this.account.accountId
          )
          .pipe(map((dismissed: boolean) => !dismissed))
      : of(false);

    this.isIaNavEnabled$ = this._featuresDataStore.getFeatureToggles().pipe(
      map((toggles) => toggles.isAllAccess(Toggles.KEYS.IA_NAV_EXPERIMENT)),
      shareReplay(1)
    );

    this.headerTheme$ = this.isIaNavEnabled$.pipe(
      map((isIaNavEnabled) =>
        isIaNavEnabled
          ? GlobalHeaderThemesEnum.ia_nav
          : GlobalHeaderThemesEnum.action_packed
      )
    );

    this._featuresDataStore
      .hasFeatureHighlightCanvas(this.featureName)
      .pipe(
        takeUntil(this._destroyed),
        filter((x) => !!x)
      )
      .subscribe(() => {
        this.featureHighlightIcon = {
          name: 'premium-feature',
          size: IconSizesEnum.medium,
          customClass: 'premium-icon',
        };
      });

    if (this.enableBulkActions) {
      this.bulkActionPrimaryTrackingAction = this._trackingActionBuilder.buildButtonTapped(
        'delete_bulk',
        this.trackingScreenName
      );
      this.bulkActionSecondaryTrackingAction = this._trackingActionBuilder.buildButtonTapped(
        'cancel',
        this.trackingScreenName
      );
    }
  }

  ngAfterViewInit() {
    this._trackingDispatcher.trackPage(
      this.trackingScreenName,
      this.trackingAdditionalProperties
    );
  }

  searchTerm(term: string) {
    this.searchQueryValue = term;

    if (!!term) {
      const params = this._entityMasterBuilder.buildDefaultSearchParams(
        term,
        this.defaultSearchParams
      );

      this._listModeUrl = !this._listModeUrl
        ? this.activeFilter
        : this._listModeUrl;

      this.trackingScreenName = TrackingScreenNamesEnum.search;

      this._navigationService.goTo([`./${SEARCH_ENTITY_ROUTE}`], {
        queryParams: params,
        relativeTo: this._route,
      });
    } else {
      const listModeUrl =
        !!this._listModeUrl &&
        ![ALL_LIST_FILTER, SEARCH_LIST_FILTER].includes(this._listModeUrl)
          ? this._listModeUrl
          : '.';

      this._listModeUrl = null;

      this.trackingScreenName = this._trackingScreenName;

      this._navigationService.goTo([listModeUrl], { relativeTo: this._route });
    }
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this.featureName
    );
  }

  showBulkActionConfirmDialog(bulkActionDialogId: string) {
    this._dialogService.openConfirmDialog(bulkActionDialogId);
  }

  onBulkActionDialogClose(
    bulkActionId: string,
    { result }: IConfirmDialogResult
  ) {
    if (result) {
      this.onBulkAction.emit(bulkActionId);
    }
  }

  showDeleteConfirmDialog() {
    this._dialogService.openConfirmDialog(this.deleleConfirmDialogId);
  }

  onDeleteConfirmClose({ result }: IConfirmDialogResult) {
    if (result) {
      this.onDeleteConfirmed.emit();
    }
  }

  ngOnDestroy() {
    this._dialogService.closeConfirmDialog(this.deleleConfirmDialogId);
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      this.featureName
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
